import { MacWindow } from './MacWindow';
import React, { useState } from 'react';
import { 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  ShieldAlert, 
  Maximize, 
  Image as ImageIcon, 
  Barcode, 
  Type, 
  FileCheck, 
  Sparkles, 
  X,
  ChevronRight,
  Zap
} from 'lucide-react';
import { VdpTemplate, CsvData, ImpositionLayout } from '../../types';

interface PreflightInspectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: VdpTemplate;
  csvData?: CsvData | null;
  layout?: ImpositionLayout | null;
  onAutoFix?: (type: string, data?: any) => void;
}

export interface PreflightIssue {
  id: string;
  category: 'safe_zone' | 'image_dpi' | 'barcode' | 'text' | 'csv' | 'imposition';
  severity: 'error' | 'warning' | 'info';
  title: string;
  description: string;
  elementId?: string;
  autoFixable?: boolean;
}

export const PreflightInspectorModal: React.FC<PreflightInspectorModalProps> = ({
  isOpen,
  onClose,
  template,
  csvData,
  layout,
  onAutoFix
}) => {
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'error' | 'warning'>('all');

  if (!isOpen) return null;

  // Perform full-scope pre-flight analysis
  const issues: PreflightIssue[] = [];

  const templateWidth = template.width || 300;
  const templateHeight = template.height || 300;
  const safeZoneMargin = layout?.safeZone ?? 8.5; // in pt

  // 1. Safe Zone & Margin Checks
  template.elements.forEach(el => {
    const elRight = el.x + el.width;
    const elBottom = el.y + el.height;

    if (el.x < safeZoneMargin || el.y < safeZoneMargin || elRight > (templateWidth - safeZoneMargin) || elBottom > (templateHeight - safeZoneMargin)) {
      issues.push({
        id: `safe-${el.id}`,
        category: 'safe_zone',
        severity: 'warning',
        title: `Element "${el.name || el.type}" Outside Safe Zone`,
        description: `Located at (${Math.round(el.x)}, ${Math.round(el.y)}) pt. Close to cut line (<${safeZoneMargin.toFixed(1)}pt margin). Risk of getting trimmed in die-cutting.`,
        elementId: el.id,
        autoFixable: true
      });
    }

    // 2. Barcode & QR Readability Inspection
    if (['barcode', 'qr', 'datamatrix', 'ean13', 'upca'].includes(el.type)) {
      if (el.width < 40 || el.height < 25) {
        issues.push({
          id: `barcode-size-${el.id}`,
          category: 'barcode',
          severity: 'error',
          title: `Barcode "${el.name || el.type}" Too Small`,
          description: `Dimensions (${Math.round(el.width)}×${Math.round(el.height)} pt) are below scanner readability standards (min 50×30 pt).`,
          elementId: el.id,
          autoFixable: true
        });
      }

      if (el.columnName && csvData) {
        const hasColumn = csvData.headers.includes(el.columnName);
        if (!hasColumn) {
          issues.push({
            id: `barcode-csv-${el.id}`,
            category: 'csv',
            severity: 'error',
            title: `Barcode Bound Column "${el.columnName}" Missing`,
            description: `The mapped CSV header is not present in uploaded dataset. Barcode will fail to generate on print.`,
            elementId: el.id,
            autoFixable: false
          });
        }
      }
    }

    // 3. Image Resolution & Source Check
    if (el.type === 'image') {
      if (!el.src && !el.staticValue && !el.columnName) {
        issues.push({
          id: `img-missing-${el.id}`,
          category: 'image_dpi',
          severity: 'error',
          title: `Image Layer "${el.name || 'Image'}" Missing Asset`,
          description: `No image URL or static file assigned. Placeholder box will appear in export.`,
          elementId: el.id,
          autoFixable: false
        });
      }
    }

    // 4. Text Unmapped / Overlap Inspection
    if (['text', 'uppercase', 'currency', 'date'].includes(el.type)) {
      const textContent = el.content || el.text || el.staticValue || '';
      if (!el.columnName && !textContent) {
        issues.push({
          id: `text-empty-${el.id}`,
          category: 'text',
          severity: 'warning',
          title: `Text Layer "${el.name || 'Text'}" Has Empty Content`,
          description: `No text value or CSV binding set. Layer is currently invisible.`,
          elementId: el.id,
          autoFixable: true
        });
      }
    }
  });

  // 5. CSV Dataset Completeness Inspection
  if (csvData) {
    if (csvData.rows.length === 0) {
      issues.push({
        id: 'csv-no-rows',
        category: 'csv',
        severity: 'error',
        title: 'Uploaded CSV Has 0 Records',
        description: 'No variable rows detected. Variable sticker generation requires at least 1 record.',
        autoFixable: false
      });
    }

    // Check for missing values in mapped columns
    const mappedElements = template.elements.filter(e => e.columnName);
    mappedElements.forEach(el => {
      if (el.columnName && csvData.headers.includes(el.columnName)) {
        const emptyCount = csvData.rows.filter(r => !r[el.columnName!] || String(r[el.columnName!]).trim() === '').length;
        if (emptyCount > 0) {
          issues.push({
            id: `csv-empty-${el.columnName}`,
            category: 'csv',
            severity: 'warning',
            title: `CSV Column "${el.columnName}" Has ${emptyCount} Empty Rows`,
            description: `${emptyCount} of ${csvData.rows.length} records have missing data for this variable field.`,
            elementId: el.id,
            autoFixable: false
          });
        }
      }
    });
  } else if (template.elements.some(e => e.columnName)) {
    issues.push({
      id: 'csv-not-loaded',
      category: 'csv',
      severity: 'warning',
      title: 'Variable Mappings Active But No CSV Uploaded',
      description: 'Design contains mapped variable text/barcodes. Upload CSV in Data step to populate production batch.',
      autoFixable: false
    });
  }

  const errorsCount = issues.filter(i => i.severity === 'error').length;
  const warningsCount = issues.filter(i => i.severity === 'warning').length;

  const filteredIssues = issues.filter(i => {
    if (selectedFilter === 'error') return i.severity === 'error';
    if (selectedFilter === 'warning') return i.severity === 'warning';
    return true;
  });

  return (
    <MacWindow isOpen={isOpen} onClose={onClose} title="Pre-Flight Quality Inspector" maxWidth="max-w-3xl">
      <div className="flex flex-col w-full h-full overflow-hidden">
        
        {/* Header */}
        <div className="px-6 py-4 bg-zinc-950 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${errorsCount > 0 ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                Pre-Flight Quality Inspector
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  Production Grade
                </span>
              </h2>
              <p className="text-xs text-zinc-400">Automated pre-press inspection before outputting print files</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status Banner */}
        <div className="px-6 py-3 bg-zinc-900/90 border-b border-zinc-800/80 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSelectedFilter('all')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 cursor-pointer ${selectedFilter === 'all' ? 'bg-zinc-800 text-white border border-zinc-700' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              All Issues ({issues.length})
            </button>
            <button
              onClick={() => setSelectedFilter('error')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 cursor-pointer ${selectedFilter === 'error' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'text-zinc-400 hover:text-red-400'}`}
            >
              <XCircle className="w-3.5 h-3.5" /> Errors ({errorsCount})
            </button>
            <button
              onClick={() => setSelectedFilter('warning')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 cursor-pointer ${selectedFilter === 'warning' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-zinc-400 hover:text-amber-400'}`}
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Warnings ({warningsCount})
            </button>
          </div>

          <div className="text-xs text-zinc-400 font-mono">
            {errorsCount === 0 ? (
              <span className="text-emerald-400 flex items-center gap-1 font-semibold">
                <CheckCircle2 className="w-4 h-4" /> Ready for Print Press
              </span>
            ) : (
              <span className="text-red-400 flex items-center gap-1 font-semibold">
                <XCircle className="w-4 h-4" /> Action Required Before Output
              </span>
            )}
          </div>
        </div>

        {/* Issue List */}
        <div className="p-6 overflow-y-auto space-y-3 flex-1">
          {filteredIssues.length === 0 ? (
            <div className="py-12 text-center flex flex-col items-center justify-center">
              <div className="w-12 h-12 bg-emerald-500/10 text-emerald-400 rounded-full flex items-center justify-center mb-3 border border-emerald-500/20">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-zinc-100">Zero Pre-Flight Issues Detected</h3>
              <p className="text-xs text-zinc-400 mt-1 max-w-md">
                Your variable sticker design, layout safe margins, barcode readability standards, and data bindings meet print-ready standards.
              </p>
            </div>
          ) : (
            filteredIssues.map((issue) => (
              <div 
                key={issue.id} 
                className={`p-4 rounded-xl border transition-all ${
                  issue.severity === 'error' 
                    ? 'bg-red-950/20 border-red-800/40 hover:border-red-700/60' 
                    : 'bg-amber-950/20 border-amber-800/40 hover:border-amber-700/60'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0">
                      {issue.severity === 'error' ? (
                        <XCircle className="w-4 h-4 text-red-400" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                      )}
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-zinc-100 flex items-center gap-2">
                        {issue.title}
                        <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded border border-zinc-700">
                          {issue.category}
                        </span>
                      </h4>
                      <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{issue.description}</p>
                    </div>
                  </div>

                  {issue.autoFixable && onAutoFix && (
                    <button
                      onClick={() => onAutoFix(issue.category, issue.elementId)}
                      className="px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all shrink-0 cursor-pointer"
                    >
                      <Zap className="w-3 h-3" /> Auto-Fix
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-zinc-950 border-t border-zinc-800 flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            Rulebook: PDF/X-4 & ISO 12647 Print Standards
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
          >
            Close Inspector
          </button>
        </div>

      </div>
    </MacWindow>
  );
};

